import { useMemo } from "react";
import type { SWRConfiguration, SWRResponse } from "swr";

import { type ResponseStatus, type IModelMetadataResponse, type IReturn } from "src/api/Tmd.dtos";
import useApi from "src/hooks/api/useApi";

/**
 * Defines the response payload and status expected by this shared request adapter.
 */
export type TMDCreateResponse<TModel> = {
    responseStatus: ResponseStatus;

    model: TModel;
};

/**
 * Defines the typed request payload constructed by this shared adapter.
 */
export type TMDCreateRequest<
    TModel,
    TMetadataContext,
    TResponse extends TMDCreateResponse<TModel>
> = IReturn<TResponse> & {
    context: TMetadataContext;
};

/**
 * Describes projection of a response into its model value.
 */
type GetDataFn<TModel, TResponse extends TMDCreateResponse<TModel>> = (
    response?: SWRResponse<TResponse, TResponse>
) => TModel;

const getData = <TModel, TResponse extends TMDCreateResponse<TModel>>(
    response?: SWRResponse<TResponse, TResponse>
) => response?.data?.model;

const getError = <TModel, TResponse extends TMDCreateResponse<TModel>>(
    response?: SWRResponse<TResponse, TResponse>
) => (response?.error ?? response?.data)?.responseStatus;

const getAdditionalState = <TModel, TResponse extends TMDCreateResponse<TModel>>(
    _response?: SWRResponse<TResponse, TResponse>
) => ({});

/**
 * Loads an initial model through a context-bearing POST request.
 *
 * Postconditions: Falsy constructor or context disables the request. Returned state projects the
 * response model and status without adding local persistence behavior. The server endpoint
 * determines the meaning of the initial-model request.
 */
const useBaseCreate = <
    TModel,
    TMetadataContext,
    TResponse extends TMDCreateResponse<TModel>,
    TRequest extends TMDCreateRequest<TModel, TMetadataContext, TResponse>
>(
    constructor: new (init?: Partial<TRequest>) => TRequest,

    context?: TMetadataContext | null | undefined,

    configuration?: SWRConfiguration<TResponse, TResponse> | null | undefined
) => {
    const getRequest = useMemo(
        () => {
            if (!constructor || !context) {
                return undefined;
            }

            return (): IReturn<TResponse> =>
                new constructor({
                    context
                } as Partial<TRequest>);
        },
        [constructor, context]
    );

    return useApi(
        "post",
        getRequest,
        { getData: getData as GetDataFn<TModel, TResponse>, getError, getAdditionalState },
        configuration
    );
};

export default useBaseCreate;
